import { useMemo, useState } from "react";
import {
	ChevronDownIcon,
	ChevronRightIcon,
	CheckCircleIcon,
	ExclamationTriangleIcon,
} from "@heroicons/react/24/solid";
import { handleEnterSpaceKey } from "../../app/keyboardUtils";
import { stepProgress } from "./rosterImportUtils";
import { ImportProgress } from "./ImportProgressBar";

interface ImportProgressHistoryProps {
	progress?: ImportProgress | null;
	includeActionNeededStep?: boolean;
}

type StepItem = ImportProgress & { key: string };

export default function ImportProgressHistory({
	progress,
	includeActionNeededStep,
}: ImportProgressHistoryProps) {
	const [showProgressHistory, setShowProgressHistory] = useState(false);

	function toggleShowProgressHistory() {
		setShowProgressHistory((s) => !s);
	}

	const steps = useMemo<StepItem[]>(() => {
		return Object.entries(stepProgress)
			.filter(([key]) => {
				if (key === "resolveStudentConflicts") {
					return includeActionNeededStep === true;
				}
				return true;
			})
			.map(([key, value]) => ({
				key,
				...value,
			}));
	}, [includeActionNeededStep]);

	const currentStepIndex = useMemo(() => {
		const phase = progress?.phase?.trim();
		if (!phase) return -1;

		const idx = steps.findIndex((s) => s.phase === phase);
		return idx;
	}, [steps, progress?.phase]);

	return (
		<section>
			<div
				className="flex items-center cursor-pointer select-none"
				onClick={toggleShowProgressHistory}
				role="button"
				tabIndex={0}
				onKeyDown={(e) => handleEnterSpaceKey(e, toggleShowProgressHistory)}
				aria-expanded={showProgressHistory}>
				{showProgressHistory ? (
					<ChevronDownIcon className="size-4" aria-hidden="true" />
				) : (
					<ChevronRightIcon className="size-4" aria-hidden="true" />
				)}
				<h3 className="text-sm text-gray-700 py-2 ml-3">Import Progress</h3>
			</div>

			{showProgressHistory && (
				<div className="pl-2">
					<ol className="relative border-l border-gray-200">
						{steps.map((s, i) => {
							const isFinalStep = s.key === "complete";
							const isFirstStep = s.key === "importCourses";

							const isCurrent = i === currentStepIndex;

							const isComplete = currentStepIndex >= 0 && i < currentStepIndex;

							const showFinalAsComplete =
								isFinalStep && currentStepIndex >= 0 && i <= currentStepIndex;

							const isPaused = !isFinalStep && isCurrent && !!progress?.pause;

							const isResolveStudentConflicts = s.key === "resolveStudentConflicts";

							const spacingClass = isFirstStep
								? "pb-3"
								: isFinalStep
									? "pt-3"
									: "py-3";

							const textClass =
								isComplete || showFinalAsComplete
									? "text-green-700"
									: isPaused
										? "text-yellow-600"
										: isCurrent
											? "text-gray-900"
											: "text-gray-600";

							return (
								<li key={s.key} className={`flex ml-4 ${spacingClass}`}>
									<span className="absolute -left-2.5 inline-flex size-5 items-center justify-center rounded-full bg-white">
										{isComplete || showFinalAsComplete ? (
											<CheckCircleIcon
												className="size-5 text-green-500"
												aria-hidden="true"
											/>
										) : isPaused ? (
											<ExclamationTriangleIcon
												className="size-5 text-yellow-500"
												aria-hidden="true"
											/>
										) : isCurrent ? (
											<span className="size-3 rounded-full bg-primary-500 ring-4 ring-primary-100" />
										) : (
											<span className="size-3 rounded-full bg-gray-300" />
										)}
									</span>

									<div className={`text-sm ${textClass}`}>
										{isResolveStudentConflicts
											? stepProgress.resolveStudentConflicts.phase
											: s.phase}
									</div>
									{isResolveStudentConflicts && (
										<div className="text-xs text-gray-600 self-center">
											<span className="mx-1">-</span>
											<span>
												{stepProgress.resolveStudentConflicts.message}
											</span>
										</div>
									)}
								</li>
							);
						})}
					</ol>
				</div>
			)}
		</section>
	);
}
